import { z } from 'zod';

/**
 * THE TWELVE COLOURS AN OBJECT MAY WEAR (`docs/decisions/ui-foundation.md`, E9).
 *
 * iOS and macOS System Settings put a small coloured rounded tile with a white glyph
 * in front of every row, and that is what makes a list of twenty settings scannable:
 * the colour is a HANDLE for finding a row again, not a status and not decoration.
 * Our sidebar has the same problem with fourteen object rows, so an object carries
 * one of these and `ui/ObjectTile` draws it.
 *
 * A CLOSED SET, LIKE THE SELECT-OPTION TONES AND FOR THE SAME REASON. Free colour
 * turns a sidebar into confetti and makes a theme flip a guessing game; these twelve
 * are Apple's own system colours, each with a published light value, dark value and
 * increased-contrast value, so every one of them has an answer in every appearance
 * the product supports. The RGB triples live in `web/src/ui/tokens.css` as
 * `--color-tile-<name>`; this module owns only the NAMES, because they are what
 * crosses the wire and what a database column has to be constrained to.
 *
 * NULL IS A REAL ANSWER. An object with no colour gets the neutral graphite tile
 * (`--color-tile-none`), so every row in the column still has a tile and the list
 * does not comb. It is not a fifth way to be invisible.
 */
export const OBJECT_COLORS = [
  'blue',
  'green',
  'indigo',
  'orange',
  'pink',
  'purple',
  'red',
  'teal',
  'yellow',
  'mint',
  'cyan',
  'brown',
] as const;

export type ObjectColor = (typeof OBJECT_COLORS)[number];

export const objectColorSchema = z.enum(OBJECT_COLORS);

/**
 * Whether a value off the wire, out of the database or out of a seed declaration is
 * one of the twelve. The database has the same list as a CHECK constraint (migration
 * 0021), so this is the readable refusal in front of the unreadable one.
 */
export function isObjectColor(value: unknown): value is ObjectColor {
  return typeof value === 'string' && (OBJECT_COLORS as readonly string[]).includes(value);
}

/**
 * The CSS custom property that paints a tile. `null` resolves to the graphite tile,
 * which is what an object without a colour gets.
 */
export function objectTileVariable(color: ObjectColor | null): string {
  return color === null ? '--color-tile-none' : `--color-tile-${color}`;
}
